import { useEffect, useRef, useState } from 'react';
import { createMeetingPopupDeviceBridge } from '../services/meetingWindow/popupDeviceBridge';
import { getMeetingPopupParentOrigin, isMeetingPopup } from '../services/meetingWindow/popupMode';
import { HANDSHAKE_TIMEOUT_MS } from './usePopupHostAuth';

/**
 * How long to wait for the opener's settings before starting the meeting
 * anyway. Deliberately long enough to be noticed and reported rather than to
 * quietly paper over a broken handshake: joining with the wrong settings is the
 * worse failure. Shares the auth leg's deadline since it is the same handshake.
 */
const SETTINGS_HANDSHAKE_TIMEOUT_MS = HANDSHAKE_TIMEOUT_MS;

/**
 * Meet Settings bridge for the meeting popup.
 *
 * Forward: applies the host's settings (sent by the opener right after the auth
 * handshake) into this popup's storage. This is what makes the host's prejoin,
 * start-muted and device choices actually take effect, since the popup builds
 * its Jitsi config from its own origin's storage (SM-1187/1188/1190).
 *
 * Reverse: sends the popup's in-meeting device selection back to the opener so
 * the host Meet Settings panel reflects it.
 *
 * Returns whether the meeting may initialize yet: `true` once the host settings
 * have been applied, the timeout has elapsed, or we're not in a popup at all.
 * Callers must hold meeting initialization until then, because prejoin and
 * start-muted are read only when the Jitsi iframe is created and cannot be
 * re-applied after join.
 */
export const useMeetingPopupDeviceBridge = (): { settingsReady: boolean } => {
  const [settingsReady, setSettingsReady] = useState(() => !isMeetingPopup());
  const settledRef = useRef(false);

  useEffect(() => {
    if (!isMeetingPopup()) {
      return;
    }
    const opener = window.opener as Window | null;
    const parentOrigin = getMeetingPopupParentOrigin();
    if (!(opener && parentOrigin)) {
      setSettingsReady(true);
      return;
    }

    let timeoutId: number | undefined;

    const settle = () => {
      if (settledRef.current) {
        return;
      }
      settledRef.current = true;
      // Stop the deadline from reporting a failed handshake after a successful one.
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      setSettingsReady(true);
    };

    timeoutId = window.setTimeout(() => {
      console.warn('[SettingsBridge/popup] host settings did not arrive; using local defaults');
      settle();
    }, SETTINGS_HANDSHAKE_TIMEOUT_MS);

    const teardown = createMeetingPopupDeviceBridge(opener, parentOrigin, settle);

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      teardown();
    };
  }, []);

  return { settingsReady };
};
